import { Application, BasePlugin } from '@goodboydigital/astro';
import { ISkeletonData } from '@pixi-spine/base';

import { App } from '../App';
import { CharacterData } from '../game/entities/CharacterEntity';
import { ChassisData } from '../game/entities/ChassisEntity';
import { DecoData } from '../game/entities/DecoEntity';
import { TrailData } from '../game/entities/TrailEntity';
import { VehicleData } from '../game/entities/VehicleEntity';
import { WheelData } from '../game/entities/WheelEntity';
import { ChunkData } from '../game/systems/ChunkSystem';
import { LevelData } from '../game/systems/LevelSystem';
import { Random } from '../utils/Random';

interface AvailableVehicleParts
{
    chassis: string[],
    character: string[],
    wheel: string[],
    trail: string[],
    deco: string[],
}

type VehiclePartType = keyof AvailableVehicleParts;

export interface ColorMeta
{
    val: string,
    coinCost: number,
    diamondCost: number,
    unlocked: boolean,
    tier: number
}

/**
 * App plugin for dealing with data files (.json, .jsz) from assets folder
 */
export class DataPlugin implements BasePlugin
{
    public app: App;
    private _availableVehicleParts?: AvailableVehicleParts;
    private _availableFlags?: string[];
    private _availableColors?: string[];

    constructor(app: Application)
    {
        this.app = app as App;
    }

    // ===== GET DATA =========================================================

    public getChunkData(name: string): ChunkData
    {
        return this.app.assets.getData<ChunkData>(`chunk-${name}`);
    }

    public getLevelData(name: string): LevelData
    {
        return this.app.assets.getData<LevelData>(`level-${name}`);
    }

    public getChassisData(name: string): ChassisData
    {
        return this.app.assets.getData<ChassisData>(`chassis-${name}`);
    }

    public getCharacterData(name: string): CharacterData
    {
        return this.app.assets.getData<CharacterData>(`character-${name}`);
    }

    public getWheelData(name: string): WheelData
    {
        return this.app.assets.getData<WheelData>(`wheel-${name}`);
    }

    public getTrailData(name: string): TrailData
    {
        return this.app.assets.getData<TrailData>(`trail-${name}`);
    }

    public getDecoData(name: string): DecoData
    {
        return this.app.assets.getData<DecoData>(`deco-${name}`);
    }

    public getData<T>(type: string, name: string): T | null
    {
        if (type === 'bike') type = 'chassis';

        if (!this.app.assets.hasData(`${type}-${name}`)) return null;

        return this.app.assets.getData<T>(`${type}-${name}`);
    }

    public getSkeletonData(name: string): ISkeletonData
    {
        const data = this.app.assets.getData<any>(name);

        if (!data.bones) throw new Error(`Invalid skeleton data: ${name}`);

        return data;
    }

    public getColorData(color: string): ColorMeta
    {
        const colorData = this.app.assets.getData<ColorMeta[]>('colors');

        for (const item of colorData) if (item.val === color) return item;

        throw new Error(`Color not available: ${color}`);
    }

    // ===== GET LISTS OF AVAILABLE PARTS =====================================

    public getAvailableChassis(): string[]
    {
        return this.getAvailableVehiclePartsByType('chassis');
    }

    public getUnlockedChassis(): string[]
    {
        return this.getUnlockedVehiclePartsByType('chassis');
    }

    public getAvailableWheels(): string[]
    {
        return this.getAvailableVehiclePartsByType('wheel');
    }

    public getUnlockedWheels(): string[]
    {
        return this.getUnlockedVehiclePartsByType('wheel');
    }

    public getAvailableCharacters(): string[]
    {
        return this.getAvailableVehiclePartsByType('character');
    }

    public getUnlockedCharacters(): string[]
    {
        return this.getUnlockedVehiclePartsByType('character');
    }

    public getAvailableTrails(): string[]
    {
        return this.getAvailableVehiclePartsByType('trail');
    }

    public getUnlockedTrails(): string[]
    {
        return this.getUnlockedVehiclePartsByType('trail');
    }

    public getAvailableDecos(): string[]
    {
        return this.getAvailableVehiclePartsByType('deco');
    }

    public getUnlockedDecos(): string[]
    {
        return this.getUnlockedVehiclePartsByType('deco');
    }
    
    public getAvailableColors(): string[]
    {
        if (this._availableColors) return this._availableColors;

        const result: string[] = [];

        // Colours are all in a single file, read from there
        this.app.assets.getData<ColorMeta[]>('colors').forEach((data) => result.push(data.val));

        // Cache results
        this._availableColors = result;

        return result;
    }
    
    public getAvailableFlags(): string[]
    {
        if (this._availableFlags) return this._availableFlags;
        const result = [];

        // There is no data for flags, only textures
        for (const f in this.app.assets.textures)
        {
            if (f.startsWith('flag-')) result.push(f.replace('flag-', ''));
        }

        // Sort lists alphabetically
        result.sort();

        // Cache results
        this._availableFlags = result;

        return result;
    }

    // ===== AVAILABILITY CHECKS ==============================================

    public isChassisAvailable(name: string): boolean
    {
        return this.isVehiclePartAvailable('chassis', name);
    }

    public isWheelAvailable(name: string): boolean
    {
        return this.isVehiclePartAvailable('wheel', name);
    }

    public isCharacterAvailable(name: string): boolean
    {
        return this.isVehiclePartAvailable('character', name);
    }

    public isTrailAvailable(name: string): boolean
    {
        return this.isVehiclePartAvailable('trail', name);
    }

    public isDecoAvailable(name: string): boolean
    {
        return this.isVehiclePartAvailable('deco', name);
    }

    public isColorAvailable(color: string): boolean
    {
        const colors = this.getAvailableColors();

        return colors.includes(color);
    }

    public isFlagAvailable(flag: string): boolean
    {
        const flags = this.getAvailableFlags();

        return flags.includes(flag);
    }

    // ===== GET RANDOM DATA ==================================================

    public getRandomChassis(tier?: number): string
    {
        return Random.pick(...this.getAvailableVehiclePartsByType('chassis', tier));
    }

    public getRandomUnlockedChassis(): string
    {
        return Random.pick(...this.getUnlockedVehiclePartsByType('chassis'));
    }

    public getRandomWheel(tier?: number): string
    {
        return Random.pick(...this.getAvailableVehiclePartsByType('wheel', tier));
    }

    public getRandomUnlockedWheel(): string
    {
        return Random.pick(...this.getUnlockedVehiclePartsByType('wheel'));
    }

    public getRandomCharacter(tier?: number): string
    {
        return Random.pick(...this.getAvailableVehiclePartsByType('character', tier));
    }

    public getRandomUnlockedCharacter(): string
    {
        return Random.pick(...this.getUnlockedVehiclePartsByType('character'));
    }

    public getRandomTrail(tier?: number): string
    {
        return Random.pick(...this.getAvailableVehiclePartsByType('trail', tier));
    }

    public getRandomUnlockedTrail(): string
    {
        return Random.pick(...this.getUnlockedVehiclePartsByType('trail'));
    }

    public getRandomDeco(tier?: number): string
    {
        return Random.pick(...this.getAvailableVehiclePartsByType('deco', tier));
    }

    public getRandomUnlockedDeco(): string
    {
        return Random.pick(...this.getUnlockedVehiclePartsByType('deco'));
    }

    public getRandomColor(tier = Infinity): string
    {
        const colorData = this.app.assets.getData<ColorMeta[]>('colors');
        const selected: string[] = [];

        for (const color of colorData)
        {
            if (color.tier <= tier) selected.push(color.val);
        }

        return Random.pick(...selected);
    }

    public getRandomUnlockedColor(): string
    {
        const colorData = this.app.assets.getData<ColorMeta[]>('colors');
        const selected: string[] = [];

        for (const color of colorData)
        {
            if (color.unlocked) selected.push(color.val);
        }

        return Random.pick(...selected);
    }

    public getRandomFlag(): string
    {
        return Random.pick(...this.getAvailableFlags());
    }

    // ===== EXTRA UTILITIES ==================================================

    /**
     * Check fundamental parts of a vehicle data and return if data is valid or not
     * Only chassis and wheels should be checked, because the other things like character and trails
     * cane be swaped freely, if needed
     * @param data - Vehicle data to be checked
     * @returns
     */
    public validateVehicle(data: VehicleData): boolean
    {
        if (!this.isWheelAvailable(data.wheelLeft)) return false;
        if (!this.isWheelAvailable(data.wheelRight)) return false;
        if (!this.isChassisAvailable(data.chassis)) return false;

        return true;
    }

    public getAvailableVehicleParts(): AvailableVehicleParts
    {
        if (this._availableVehicleParts) return this._availableVehicleParts;

        const availableParts: AvailableVehicleParts = {
            chassis: [],
            character: [],
            wheel: [],
            trail: [],
            deco: [],
        };

        // Search assets for available data
        for (const f in this.app.assets.data)
        {
            const data = this.app.assets.data[f];
            const available = data.available !== undefined ? !!data.available : true;

            if (!available) continue;

            if (f.startsWith('character-')) availableParts.character.push(f.replace('character-', ''));
            if (f.startsWith('wheel-')) availableParts.wheel.push(f.replace('wheel-', ''));
            if (f.startsWith('chassis-')) availableParts.chassis.push(f.replace('chassis-', ''));
            if (f.startsWith('trail-')) availableParts.trail.push(f.replace('trail-', ''));
            if (f.startsWith('deco-')) availableParts.deco.push(f.replace('deco-', ''));
        }
        // Sort available parts alphabetically
        availableParts.chassis.sort().sort(this.sortUnlocked('chassis'));
        availableParts.character.sort(this.sortUnlocked('character'));
        availableParts.wheel.sort(this.sortUnlocked('wheel'));
        availableParts.trail.sort(this.sortUnlocked('trail'));
        availableParts.deco.sort(this.sortUnlocked('deco'));

        // Cache available parts
        this._availableVehicleParts = availableParts;

        return availableParts;
    }

    private sortUnlocked(prefix: string): (a: string, b: string) => number
    {
        return (a: string, b: string) =>
        {
            const aData = this.app.assets.getData(`${prefix}-${a}`) as any;
            const bData = this.app.assets.getData(`${prefix}-${b}`) as any;

            if (aData.unlocked && !bData.unlocked) return -1;
            if (!aData.unlocked && bData.unlocked) return 1;

            return 0;
        };
    }

    private getAvailableVehiclePartsByType(type: VehiclePartType, tier = Infinity): string[]
    {
        const availableParts = this.getAvailableVehicleParts();
        const result: string[] = [];

        for (const part of availableParts[type])
        {
            const data = this.app.assets.getData<any>(`${type}-${part}`);
            const dataTier = data.tier !== undefined ? data.tier : 0;

            if (dataTier <= tier) result.push(part);
        }

        return result;
    }

    private getUnlockedVehiclePartsByType(type: VehiclePartType): string[]
    {
        const availableParts = this.getAvailableVehicleParts();
        const result: string[] = [];

        for (const part of availableParts[type])
        {
            const data = this.app.assets.getData<any>(`${type}-${part}`);

            if (data.unlocked) result.push(part);
        }

        return result;
    }

    private isVehiclePartAvailable(type: VehiclePartType, name: string): boolean
    {
        const availableParts = this.getAvailableVehicleParts();

        return availableParts[type].includes(name);
    }
}
